// Failures in plain words (02-building-blocks.md, "Error wording"): what went wrong is classified,
// then worded for what the person was doing, with a reference code. People never see a server's
// text, code, status or address; the full error goes to the console once, with its code.
import { t, type Key } from '../i18n';
import { PRODUCT } from './product';

export type Context = 'general' | 'join' | 'load' | 'send' | 'upload' | 'search' | 'start' | 'account' | 'call' | 'save';

export type Kind =
  | 'NW' | 'RL' | 'SO' | 'FB' | 'IV' | 'NF' | 'TL' | 'QU' | 'SV' | 'IP'
  | 'CF' | 'EX' | 'NE' | 'BA' | 'MS' | 'DD' | 'ND' | 'DB' | 'XX';

export interface Failure {
  kind: Kind;
  text: string;
  /** "NF7K2Q"; absent for desyr's own sentences. */
  reference?: string;
}

/** desyr's own sentence ("Enter a name."): shown as it is, without a reference code. */
export class Said extends Error {}

/** A failure with what the classifier needs; services and the chat client both map to this. */
export interface Answer {
  status?: number;
  /** Matrix `errcode` or desyr's `code`. */
  code?: string;
  /** Seconds to wait, from Retry-After or `retry_after_ms`. */
  retryAfter?: number;
  /** The server's own text: read for invite-only joins, never shown. */
  serverText?: string;
}

const DESYR_KINDS: Record<string, Kind> = {
  'desyr.busy': 'SV',
  'desyr.unavailable': 'SV',
  'desyr.auth.sign_in': 'SO',
  'desyr.auth.not_admin': 'FB',
  'desyr.request.too_large': 'TL',
  // Invalid requests (94, Every error code; v1.58.56): the "new device" calls' Origin, a body that isn't JSON, a field missing.
  'desyr.request.not_json': 'IP',
  'desyr.request.origin': 'IP',
  'desyr.request.bad': 'IP',
  'desyr.people.name_in_use': 'EX',
};

function answerOf(error: unknown): Answer {
  const e = error as Record<string, any> | null;
  if (!e || typeof e !== 'object') return {};
  const data = (e.data ?? {}) as Record<string, any>;
  const ms = e.retryAfterMs ?? data.retry_after_ms;
  return {
    status: e.httpStatus ?? e.status,
    code: e.errcode ?? data.errcode ?? e.code ?? data.code,
    retryAfter: e.retryAfter ?? (typeof ms === 'number' ? ms / 1000 : undefined),
    serverText: data.error ?? e.message,
  };
}

const NETWORK_NAMES = ['ConnectionError', 'AbortError', 'TimeoutError', 'NetworkError'];

export function classify(error: unknown, context: Context = 'general'): Kind {
  const name = (error as Error | undefined)?.name;
  if (context === 'call' || context === 'start') {
    if (name === 'NotAllowedError' || name === 'SecurityError') return 'DD';
    if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'ND';
    if (name === 'NotReadableError') return 'DB';
  }
  if ((error as { kind?: string } | undefined)?.kind === 'MS') return 'MS'; // mail needs its password again
  if (name && NETWORK_NAMES.includes(name)) return 'NW';
  if (error instanceof TypeError && !answerOf(error).status) return 'NW'; // a failed fetch
  const { status, code = '' } = answerOf(error);
  if (code in DESYR_KINDS) return DESYR_KINDS[code];
  if (status === 429 || code === 'M_LIMIT_EXCEEDED') return 'RL';
  if (status === 401 || ['M_UNKNOWN_TOKEN', 'M_MISSING_TOKEN', 'M_USER_DEACTIVATED'].includes(code)) return 'SO';
  if (code === 'M_RESOURCE_LIMIT_EXCEEDED') return 'QU';
  if (status === 413 || code === 'M_TOO_LARGE') return 'TL';
  if (status === 403 || code === 'M_FORBIDDEN') return 'FB';
  if (status === 404 || code === 'M_NOT_FOUND' || code === 'M_UNRECOGNIZED') return 'NF';
  if (status === 409 || status === 412) return 'CF';
  if (status !== undefined && status >= 500) return 'SV';
  if (status === 400 || status === 422) return 'IP';
  return 'XX';
}

/** "Too many tries. Wait N seconds …" from the server's delay (02, rate limit). */
export function waitSentence(seconds?: number): string {
  if (seconds === undefined || seconds > 300) return t('error.waitMoment');
  if (seconds < 90) return t('error.waitSeconds', { n: Math.max(1, Math.ceil(seconds)) });
  return t('error.waitMinutes', { n: Math.ceil(seconds / 60) });
}

const UNKNOWN: Record<Context, Key> = {
  general: 'error.general',
  join: 'error.general',
  load: 'error.load',
  send: 'error.send',
  upload: 'error.upload',
  search: 'error.search',
  start: 'error.general',
  account: 'error.account',
  call: 'error.call',
  save: 'error.save',
};

const KIND_WORDS: Partial<Record<Kind, Key>> = {
  NW: 'error.network',
  SO: 'error.signedOut',
  MS: 'error.mailSignedOut',
  CF: 'error.conflict',
  EX: 'error.exists',
  NE: 'error.notEmpty',
  BA: 'error.badAddress',
  QU: 'error.noSpace',
  DD: 'error.deviceRefused',
  ND: 'error.noDevice',
  DB: 'error.deviceBusy',
};

function words(kind: Kind, context: Context, answer: Answer): string {
  const reading = context === 'load' || context === 'search';
  if (context === 'join') {
    switch (kind) {
      case 'IV': return t('error.joinInviteOnly');
      case 'NW': case 'SV': return t('error.joinNetwork');
      case 'RL': return waitSentence(answer.retryAfter);
      case 'SO': return t('error.signedOut');
      case 'XX': return t(UNKNOWN.join);
      default: return t('error.joinOther');
    }
  }
  switch (kind) {
    case 'SV': return t(context === 'call' ? 'error.unavailableCall' : 'error.unavailable');
    case 'RL': return waitSentence(answer.retryAfter);
    case 'FB': return t(context === 'call' ? 'error.forbiddenCall' : reading ? 'error.noAccess' : 'error.forbidden');
    case 'NF': return t(reading ? 'error.notFoundLoad' : 'error.notFound');
    case 'TL': return t(context === 'upload' ? 'error.fileTooLarge' : 'error.tooLarge');
    default: return t(KIND_WORDS[kind] ?? UNKNOWN[context]);
  }
}

const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const known = new WeakMap<object, Failure>();

const randomTail = () => Array.from({ length: 4 }, () => ALPHABET[Math.floor(Math.random() * ALPHABET.length)]).join('');

/**
 * The failure as people see it. `own` lets an area word a failure itself (by kind or code);
 * the reference stays. The same error object keeps its code and is logged once.
 */
export function describe(
  error: unknown,
  context: Context = 'general',
  own?: (kind: Kind, answer: Answer) => string | undefined,
): Failure {
  if (error instanceof Said) return { kind: 'XX', text: error.message };
  if (error && typeof error === 'object' && known.has(error)) return known.get(error)!;
  const answer = answerOf(error);
  let kind = classify(error, context);
  if (context === 'join' && kind === 'FB' && /invite|knock/i.test(answer.serverText ?? '')) kind = 'IV';
  const reference = kind + randomTail();
  const failure = { kind, text: own?.(kind, answer) ?? words(kind, context, answer), reference };
  console.error(`[${PRODUCT}] error ${reference} (${context}):`, error);
  if (error && typeof error === 'object') known.set(error, failure);
  return failure;
}
